import { CheckmarkCircledIcon } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import styled, { css } from 'styled-components'

const StepperComponent: React.FC = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const step = parseInt(pathname[pathname.length - 1])
  const steps = [
    { title: t(`invite.orgAndUsers`), number: 1 },
    { title: t(`invite.accessRights`), number: 2 },
    { title: t(`invite.customerNumbersContracts`), number: 3 },
  ]

  return (
    <StyledStepperContainer aria-label="Invite progress">
      {steps.map(({ title, number }) => {
        const active = step === number
        const completed = step > number

        return (
          <StyledStep key={number} active={active} completed={completed} aria-current={active ? 'step' : undefined}>
            <StyledStepContent>
              <StyledStepNumber>Step {number}</StyledStepNumber>
              <StyledStepTitle>{title}</StyledStepTitle>
            </StyledStepContent>
            <StyledCheckmark aria-hidden="true" completed={completed} />
          </StyledStep>
        )
      })}
    </StyledStepperContainer>
  )
}

export const Stepper = observer(StepperComponent)

const StyledStepperContainer = styled('ol')(
  ({
    theme: {
      color: {
        border: {
          core: { highContrast },
        },
      },
    },
  }) => css`
    position: relative;
    display: flex;
    margin: 0;
    padding: 0;
    width: 100%;
    list-style: none;

    &::before {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 1px;
      content: '';
      background-color: ${highContrast.default};
    }
  `
)

const StyledStep = styled('li')<{
  active: boolean
  completed: boolean
}>(
  ({
    theme: {
      color: {
        foreground: { success, accent },
      },
      spacing: { space1, space2, space4, space5, space6 },
    },
    active,
    completed,
  }) => css`
    position: relative;
    display: flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: space-between;
    gap: ${space2}rem;
    min-width: 0;
    padding: ${space6}rem ${space5}rem ${space6}rem ${space4}rem;

    ${active || completed
      ? css`
          &::after {
            position: absolute;
            right: 0;
            bottom: 0;
            left: 0;
            height: ${space1}rem;
            content: '';
            background-color: ${completed ? success.default : accent.lowContrast.default};
          }
        `
      : null}
  `
)

const StyledStepContent = styled('div')(
  ({ theme: { spacing } }) => css`
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: ${spacing.space1}rem;
    min-width: 0;
  `
)

const StyledStepNumber = styled('span')(
  ({ theme }) => css`
    ${theme.typography.mixins.body({ size: 'five', fontWeight: 'regular' })}
    color: ${theme.color.text.body.primary};
  `
)

const StyledStepTitle = styled('span')(
  ({ theme }) => css`
    ${theme.typography.mixins.label({ size: 'three', fontWeight: 'bold' })}
    overflow: hidden;
    color: ${theme.color.text.title.primary};
    text-overflow: ellipsis;
    white-space: nowrap;
  `
)

const StyledCheckmark = styled(CheckmarkCircledIcon)<{
  completed: boolean
}>(
  ({
    theme: {
      color: {
        foreground: { success, core },
      },
    },
    completed,
  }) => css`
    flex: 0 0 auto;
    width: 1.5rem;
    height: 1.5rem;
    color: ${completed ? success.default : core.disabled};
  `
)
